/**
 * Font favourites — the families a user has starred in the font picker. Stored by the backend in one document (like template
 * favourites) so they follow the user into every publication; kept here as a small in-memory store with change listeners.
 */
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

let favorites = []; // family names, in the order they were starred
let loaded = null; // Promise<string[]>
const listeners = new Set();
const notify = () => listeners.forEach((fn) => { try { fn(favorites); } catch { /* ignore */ } });

/** Subscribe to favourite changes; returns an unsubscribe function. */
export const onFavoritesChanged = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };

/** Fetch once (safe to call repeatedly). */
export const loadFontFavorites = (force = false) => {
  if (loaded && !force) return loaded;
  loaded = (async () => {
    try {
      const { data } = await axios.get(`${API}/fonts/favorites`);
      favorites = Array.isArray(data?.families) ? data.families : [];
      notify();
    } catch (e) {
      console.warn('[fonts] favourites unavailable', e?.message || e); // eslint-disable-line no-console
    }
    return favorites;
  })();
  return loaded;
};

export const getFontFavorites = () => favorites;
export const isFontFavorite = (family) => favorites.some((f) => f.toLowerCase() === String(family || '').toLowerCase());

const persist = async (next) => {
  const prev = favorites;
  favorites = next;
  notify();
  try {
    const { data } = await axios.put(`${API}/fonts/favorites`, { families: next });
    if (Array.isArray(data?.families)) { favorites = data.families; notify(); }
    return true;
  } catch (e) {
    favorites = prev; // roll back the optimistic update
    notify();
    throw e;
  }
};

/** Star / unstar a family. Resolves to the new favourite state (true = now a favourite). */
export const toggleFontFavorite = async (family) => {
  const fam = String(family || '').trim();
  if (!fam) return false;
  const on = isFontFavorite(fam);
  await persist(on ? favorites.filter((f) => f.toLowerCase() !== fam.toLowerCase()) : [...favorites, fam]);
  return !on;
};
